import React, { Component } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles.css';

const RECOVER_KEY = 'av-auto-reloaded';

// Top-level boundary: last resort so the browser never shows a blank white page.
// Most errors caught here are transient stale-bundle races — the app was open
// while a new version deployed, so the running code briefly mismatched the
// served assets. A reload pulls the current build and clears it. We therefore
// auto-reload ONCE (guarded by sessionStorage so it can't loop). Only if the
// fresh build still errors do we show the manual "Something went wrong" screen.
class RootErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null, recovering: false }; }
  static getDerivedStateFromError(error) {
    let recovering = false;
    try { recovering = !sessionStorage.getItem(RECOVER_KEY); } catch { /* storage blocked */ }
    return { error, recovering };
  }
  componentDidCatch(error, info) {
    console.error('[RootErrorBoundary]', error, info);
    if (this.state.recovering) {
      try { sessionStorage.setItem(RECOVER_KEY, '1'); } catch { /* storage blocked */ }
      // Reload with the latest build. Wrapped in a microtask so React finishes
      // committing the error state first.
      setTimeout(() => window.location.reload(), 50);
    }
  }
  render() {
    if (this.state.error) {
      // While auto-recovering, show a calm "updating" message instead of the
      // alarming error — the reload lands within a moment.
      if (this.state.recovering) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', background: '#0D1D32', color: '#fff', gap: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Updating to the latest version…</div>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', background: '#0D1D32', color: '#fff', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#F87171' }}>Something went wrong</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', maxWidth: 320, textAlign: 'center' }}>{String(this.state.error)}</div>
          <button
            style={{ marginTop: 8, padding: '8px 20px', background: '#A1723D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => window.location.reload()}
          >Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);

if ('serviceWorker' in navigator) {
  // A deploy while this tab is open activates a new service worker in the
  // background. The page then keeps running on the old JS, and if it later
  // asks for a code-split chunk the deploy has removed, it gets a 404 and goes
  // blank. So the page does need to reload onto the new build eventually.
  //
  // It used to reload the instant control changed. That is what people were
  // reporting as "the page randomly refreshes": a deploy landed while somebody
  // was part-way through typing an invoice, and the reload took the form with
  // it. Losing typed work is a worse outcome than running a build that is a few
  // minutes old, and the stale-chunk case already has a safety net in the error
  // boundary above, which reloads when a chunk genuinely fails to load.
  //
  // THE UPDATE IS NOW NEVER APPLIED ON ITS OWN. It waits for the person to press
  // Reload, and that is the whole of it.
  //
  // The previous version tried to be clever about picking a free moment: on a
  // screen change, after thirty seconds, or when the tab regained focus, provided
  // nothing was TYPED. Every one of those was wrong in practice.
  //
  //   - Processing an invoice means reading a PDF, clicking things and choosing
  //     from dropdowns. Nothing is necessarily typed, so "nothing typed" read as
  //     "nothing to lose" while a half-finished invoice was on screen.
  //   - Coming back to the tab is not a free moment. Anybody reconciling against
  //     Sage is in and out of this tab constantly, and every return triggered it.
  //   - Thirty seconds after a deploy is not a free moment either. It is just a
  //     deadline.
  //
  // The risk this was protecting against — running a build old enough to ask for a
  // code-split chunk the deploy has removed — is already covered by the error
  // boundary above, which reloads once when a chunk genuinely fails. That net
  // catches the real failure at the moment it happens, which is worth more than
  // a pre-emptive reload that takes somebody's work with it.
  let waitingWorker = null;
  let reloaded = false;
  // Set only when WE ask the new worker to take over. controllerchange below
  // reloads on nothing else: a worker claiming clients for any other reason must
  // not be able to pull the page out from under somebody.
  let reloadRequested = false;

  // ── PRESSING RELOAD RELOADS. ────────────────────────────────────────────────
  //
  // Reported from live use: "it's not possible to press reload." The button did
  // nothing, and once pressed it was dead for good.
  //
  // It asked the waiting worker to take over and then relied on controllerchange to
  // do the reloading. With two tabs open — which is how this app is used, ops in one
  // and Sage in the other — the other tab had already reloaded and activated the new
  // worker. So in this tab there was nothing waiting: skipWaiting() on an
  // already-active worker is a no-op, the controller had changed long before, and
  // controllerchange was never going to fire again. Meanwhile `reloaded` had latched,
  // so every later press did nothing at all.
  //
  // A reload is not something to be clever about. The person has asked for the new
  // build: ask the worker to step aside, then reload regardless of whether anything
  // answers.
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    reloadRequested = true;
    try { waitingWorker?.postMessage('SKIP_WAITING'); } catch { /* nothing waiting */ }
    // controllerchange below gets there first when there IS a worker to swap; this is
    // the guarantee for when there is not.
    setTimeout(() => window.location.reload(), 250);
  };

  // A new build has finished installing and is waiting behind the running one.
  // Say so, once, and then leave it alone. The old worker keeps serving the build
  // this page was built against for as long as the page is open, so nothing is
  // broken by waiting — the page is simply a build behind, which costs nothing.
  const onUpdateWaiting = (worker) => {
    if (waitingWorker) return;
    waitingWorker = worker;
    showUpdateNotice(reload);
  };

  // Only ever the confirmation of a swap this page asked for. Without the guard,
  // a worker calling clients.claim() — which ours does on activate — reloads the
  // page whether or not anybody asked, which is how every safeguard above got
  // bypassed.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadRequested) window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      if (!reg) return;
      // Installed before this listener was attached.
      if (reg.waiting && navigator.serviceWorker.controller) onUpdateWaiting(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // An existing controller is what distinguishes an UPDATE from the
          // very first install. On a first install there is nothing to tell
          // anybody: the build they are running is already the new one.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            onUpdateWaiting(installing);
          }
        });
      });
    }).catch(() => {});
  });
}

// A small, ignorable notice rather than a modal. It is not urgent: the app
// works, it is simply a build behind, and interrupting somebody to tell them
// that is the same mistake as reloading underneath them.
// Exported only so it can be raised on demand from the console when checking how
// it looks; nothing imports it.
export function showUpdateNotice(onReload) {
  if (document.getElementById('av-update-notice')) return;
  injectUpdateNoticeStyles();
  const el = document.createElement('div');
  el.id = 'av-update-notice';
  el.setAttribute('role', 'status');
  const text = document.createElement('span');
  // Says that waiting is fine, because it is: nothing reloads until this is
  // pressed, and the page carries on working on the build it started with.
  text.textContent = 'A new version is ready — reload when you are ready.';
  const btn = document.createElement('button');
  btn.textContent = 'Reload';
  btn.className = 'av-update-notice__reload';
  btn.addEventListener('click', () => {
    // Says it heard, because a button that looks unpressed is indistinguishable from
    // one that did not work — which is exactly how the old bug read.
    btn.textContent = 'Reloading…';
    btn.disabled = true;
    onReload();
  });
  const dismiss = document.createElement('button');
  dismiss.textContent = '✕';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.className = 'av-update-notice__dismiss';
  // Dismissed means dismissed. The update still applies at the next safe
  // moment; it simply stops saying so. Re-showing a notice somebody has closed
  // is how it became the thing they complained about.
  dismiss.addEventListener('click', () => el.remove());
  el.append(text, btn, dismiss);
  document.body.appendChild(el);
}

// Styled from a stylesheet rather than inline, so the dark rules can follow the
// `.dark` class on <html> live: somebody who switches theme while the notice is
// up sees it restyle rather than sit there in the wrong palette.
//
// Reported from live use: in dark mode the notice was navy on a navy page and
// simply disappeared. Dark mode therefore INVERTS it — cream panel, navy text —
// instead of restating the page's own colours. It is the one element on screen
// that is deliberately not of the surface it sits on, which is the whole job of
// a notice you are meant to spot without being interrupted by it.
function injectUpdateNoticeStyles() {
  if (document.getElementById('av-update-notice-styles')) return;
  const style = document.createElement('style');
  style.id = 'av-update-notice-styles';
  style.textContent = `
#av-update-notice {
  position: fixed; bottom: 16px; right: 16px; z-index: 2147483647;
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 8px;
  font-family: Montserrat, system-ui, sans-serif; font-size: 11px;
  background: #0D1D32; color: #fff;
  border: 1px solid #A1723D;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
}
#av-update-notice .av-update-notice__reload {
  background: #A1723D; color: #fff; border: none; border-radius: 5px;
  padding: 4px 12px; font-size: 11px; font-weight: 600; cursor: pointer;
  font-family: inherit;
}
#av-update-notice .av-update-notice__dismiss {
  background: none; border: none; color: rgba(255,255,255,0.5);
  font-size: 12px; cursor: pointer; padding: 0 2px; line-height: 1;
}
.dark #av-update-notice {
  background: #FAF6EE; color: #0D1D32; font-weight: 600;
  border-color: #A1723D;
  box-shadow: 0 6px 22px rgba(0,0,0,0.55), 0 0 0 3px rgba(161,114,61,0.35);
}
.dark #av-update-notice .av-update-notice__reload {
  background: #0D1D32; color: #FAF6EE;
}
.dark #av-update-notice .av-update-notice__dismiss {
  color: rgba(13,29,50,0.55);
}
`;
  document.head.appendChild(style);
}
